import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, vShow as _vShow, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createBlock as _createBlock, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = ["data-test"]
const _hoisted_2 = {
  key: 0,
  class: "input-text__label tw-inline-block"
}
const _hoisted_3 = { class: "input-text__icon-container" }
const _hoisted_4 = { class: "input-text__icon-container input-text__icon-container--small" }
const _hoisted_5 = {
  class: "input-text__icon-container",
  "data-test": "icon__arrow-dropdown"
}

import CoreCalendar from 'frontcore/presentation/calendar/views/CoreCalendar.vue'
import CoreContextMenu from 'frontcore/views/context-menu/CoreContextMenu.vue'
import i18n from 'frontcore/i18n'
import InputHelperText from "frontcore/views/select/InputHelperText.vue"
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import useInputDate from 'frontcore/presentation/calendar/domain/useInputDate'
import useSelectState from 'frontcore/views/select/useSelectState'
import { CalendarModeOptions, DisabledDateFunction, InputDateViewModel } from 'frontcore/presentation/calendar/domain/types'
import { computed, reactive, ref, toRefs } from 'vue'
import { ContextMenuPosition } from 'frontcore/views/context-menu/useContextMenuPosition'
import { Nullable } from 'frontcore/presentation/types'

interface Props {
  dataTest?: string
  disableFutureDays?: boolean
  disablePastDays?: boolean
  disabled?: boolean
  disabledDays?: Nullable<DisabledDateFunction | Date[]>
  error?: string
  label?: string
  nullable?: boolean
  placeholder?: string
  range?: boolean
  selectionMode?: CalendarModeOptions
  supportingText?: string
  value?: InputDateViewModel['selectedDate']
}

export default /*@__PURE__*/_defineComponent({
  __name: 'InputDate',
  props: {
    dataTest: { default: 'core-date-picker' },
    disableFutureDays: { type: Boolean, default: false },
    disablePastDays: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    disabledDays: { default: () => [] },
    error: {},
    label: { default: '' },
    nullable: { type: Boolean, default: true },
    placeholder: {},
    range: { type: Boolean, default: false },
    selectionMode: { default: CalendarModeOptions.SELECT_DAY },
    supportingText: {},
    value: { default: null }
  },
  emits: ["input"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const {
  disableFutureDays,
  disablePastDays,
  disabled,
  disabledDays,
  error,
  nullable,
  range,
  selectionMode,
  value,
} = toRefs(props)

const { formattedDateValue } = useInputDate({ value, range, nullable, selectionMode }, emit)

const {
  chevronStyle,
  clickOutside,
  isFocused,
  showClearButton,
  stateStyleClass,
  toggleFocus,
} = useSelectState(reactive({ disabled, error, value: formattedDateValue, nullable }))

const { t } = i18n.global

const isShowingPlaceholder = computed<boolean>(() => formattedDateValue.value === '')

const selectedDateLabel = computed<string>(() => {
  if (isShowingPlaceholder.value) {
    return props.placeholder ?? t('common.messages.date')
  }
  return formattedDateValue.value
})

const rootElement = ref<HTMLDivElement | null>(null)
const selectValue = (selected: InputDateViewModel['selectedDate']) => {
  emit('input', selected)
  toggleFocus()
}

const clearSelectedDate = () => {
  const defaultValue: InputDateViewModel['selectedDate'] = range.value ? [] : null
  emit('input', defaultValue)
  emitNativeClick()
}

const emitNativeClick = () => {
  rootElement.value?.click()
}

const contextMenuAnchor = ref<HTMLElement | null>(null)
const contextMenuPositions: ContextMenuPosition[] = [
  ContextMenuPosition.bottomRight, ContextMenuPosition.bottomLeft,
  ContextMenuPosition.topRight, ContextMenuPosition.topLeft,
]

return (_ctx: any,_cache: any) => {
  const _directive_click_outside = _resolveDirective("click-outside")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    ref_key: "rootElement",
    ref: rootElement,
    class: _normalizeClass(["input-text__container core-calendar", [_unref(stateStyleClass), {'pointer': !_unref(disabled), 'input-text__state-disabled': _unref(disabled)}]]),
    "data-test": _ctx.dataTest
  }, [
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(_ctx.label), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      ref_key: "contextMenuAnchor",
      ref: contextMenuAnchor,
      class: "input-text__input-container",
      onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(toggleFocus) && _unref(toggleFocus)(...args)))
    }, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(SVGIcon, {
          class: "input-text__left-icon",
          iconName: "CalendarMonth",
          remSize: 1.25
        })
      ]),
      _createElementVNode("span", {
        class: _normalizeClass(["input-text__input", {'input-text__input--placeholder': isShowingPlaceholder.value}]),
        "data-test": "core-calendar-input"
      }, _toDisplayString(selectedDateLabel.value), 3),
      _withDirectives(_createElementVNode("div", _hoisted_4, [
        _createVNode(SVGIcon, {
          "data-test": "clear-button",
          iconName: "Close",
          colorClass: _unref(disabled) ? 'tw-fill-core-neutral-cold-50' : 'tw-fill-core-neutral-cold-40',
          remSize: 1,
          onClick: _withModifiers(clearSelectedDate, ["stop"])
        }, null, 8, ["colorClass"])
      ], 512), [
        [_vShow, _unref(showClearButton)]
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(SVGIcon, {
          class: "input-text__chevron",
          iconName: "ArrowDropDown",
          colorClass: _unref(disabled) ? 'tw-fill-core-neutral-cold-50' : 'tw-fill-core-neutral-cold-20',
          remSize: 1.25,
          style: _normalizeStyle(_unref(chevronStyle))
        }, null, 8, ["colorClass", "style"])
      ])
    ], 512),
    (_unref(isFocused))
      ? (_openBlock(), _createBlock(CoreContextMenu, {
          key: 1,
          anchor: contextMenuAnchor.value,
          positions: contextMenuPositions
        }, {
          default: _withCtx(() => [
            _createVNode(CoreCalendar, {
              disableFutureDays: _unref(disableFutureDays),
              disablePastDays: _unref(disablePastDays),
              disabled: _unref(disabled),
              disabledDays: _unref(disabledDays),
              range: _unref(range),
              selectionMode: _unref(selectionMode),
              value: _unref(value),
              onInput: selectValue
            }, null, 8, ["disableFutureDays", "disablePastDays", "disabled", "disabledDays", "range", "selectionMode", "value"])
          ], undefined, true),
          _: 1
        }, 8, ["anchor"]))
      : _createCommentVNode("", true),
    _createVNode(InputHelperText, {
      class: _normalizeClass({invisible: _unref(isFocused)}),
      error: _unref(error),
      supportingText: _ctx.supportingText
    }, null, 8, ["class", "error", "supportingText"])
  ], 10, _hoisted_1)), [
    [_directive_click_outside, _unref(clickOutside)]
  ])
}
}

})